import { useMemo, useState } from 'react';
import type { RouteAnalyticsView } from '../../domain/models/contracts';
import type { UpdateTicketPriceCommand } from '../../domain/models/contracts';
import { CostBreakdown } from './analytics/CostBreakdown';
import { DemandWaterfall } from './analytics/DemandWaterfall';
import { FareControls } from './analytics/FareControls';
import { KpiCards } from './analytics/KpiCards';
import { LeakageBreakdown } from './analytics/LeakageBreakdown';
import { PerformanceTrendChart } from './analytics/PerformanceTrendChart';
import { RouteSelector } from './analytics/RouteSelector';
import { computeKpis } from './analytics/routeMetrics';
import './RouteAnalyticsInspector.css';

export interface RouteAnalyticsInspectorProps {
  /**
   * Latest per-route analytics from the worker's TICK_DAILY event. The
   * inspector is purely presentational: it holds no simulation state.
   */
  readonly analytics: readonly RouteAnalyticsView[];
  /** Forwards UPDATE_TICKET_PRICE to the simulation worker bridge. */
  readonly onUpdateTicketPrice: (command: UpdateTicketPriceCommand) => void;
  /** Route to show first (e.g. when opened from the map). */
  readonly initialLegId?: string | undefined;
}

export function RouteAnalyticsInspector({
  analytics,
  onUpdateTicketPrice,
  initialLegId,
}: RouteAnalyticsInspectorProps) {
  const [requestedLegId, setRequestedLegId] = useState<string | undefined>(initialLegId);

  // Fall back to the first route if the chosen leg no longer exists
  // (e.g. the route was cancelled between ticks).
  const selected = useMemo<RouteAnalyticsView | undefined>(() => {
    const match = analytics.find((route) => route.legId === requestedLegId);
    return match ?? analytics[0];
  }, [analytics, requestedLegId]);

  const kpis = useMemo(() => (selected ? computeKpis(selected) : undefined), [selected]);

  if (!selected || !kpis) {
    return (
      <div className="route-inspector route-inspector--empty">
        <h1 className="ri-title">Route Analytics</h1>
        <p className="ri-empty">
          No published routes yet. Create a flight leg and run the simulation for a day to see
          analytics.
        </p>
      </div>
    );
  }

  return (
    <div className="route-inspector">
      <header className="ri-header">
        <div>
          <h1 className="ri-title">Route Analytics</h1>
          <p className="ri-subtitle">
            {selected.origin} → {selected.destination} · leg {selected.legId}
          </p>
        </div>
        <RouteSelector
          routes={analytics}
          selectedLegId={selected.legId}
          onSelect={setRequestedLegId}
        />
      </header>

      <KpiCards kpis={kpis} />

      <div className="ri-grid">
        <FareControls route={selected} onUpdateTicketPrice={onUpdateTicketPrice} />
        <div className="ri-stack-col">
          <CostBreakdown route={selected} />
          <DemandWaterfall demand={selected.demand} />
          <LeakageBreakdown demand={selected.demand} />
        </div>
      </div>

      <PerformanceTrendChart history={selected.history} />
    </div>
  );
}
